import React, { useState, useEffect, useRef } from "react";

interface InputDialogProps {
	isOpen: boolean;
	prompt: string;
	onSubmit: (value: string) => void;
	onCancel: () => void;
}

const InputDialog: React.FC<InputDialogProps> = ({
	isOpen,
	prompt,
	onSubmit,
	onCancel,
}) => {
	const [inputValue, setInputValue] = useState("");
	const inputRef = useRef<HTMLInputElement>(null);

	useEffect(() => {
		if (isOpen && inputRef.current) {
			inputRef.current.focus();
			setInputValue("");
		}
	}, [isOpen]);

	const handleSubmit = (e: React.FormEvent) => {
		e.preventDefault();
		onSubmit(inputValue);
		setInputValue("");
	};

	const handleKeyDown = (e: React.KeyboardEvent) => {
		if (e.key === "Escape") {
			onCancel();
		}
	};

	if (!isOpen) return null;

	return (
		<div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fade-in">
			<div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl border border-gray-200 dark:border-gray-700 w-full max-w-md mx-auto animate-slide-up transition-colors duration-200 overflow-hidden">
				{/* Header */}
				<div className="px-5 py-4 bg-python-blue-50 dark:bg-python-blue-900/20 border-b border-gray-200 dark:border-gray-700">
					<div className="flex items-center gap-3">
						<div className="w-8 h-8 bg-python-blue-100 dark:bg-python-blue-900/30 rounded-lg flex items-center justify-center shadow-soft">
							<svg
								className="w-5 h-5 text-python-blue-600 dark:text-python-blue-400"
								fill="none"
								stroke="currentColor"
								viewBox="0 0 24 24"
							>
								<path
									strokeLinecap="round"
									strokeLinejoin="round"
									strokeWidth="2"
									d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
								></path>
							</svg>
						</div>
						<div>
							<h3 className="text-base font-semibold text-gray-900 dark:text-white">
								Program Input Required
							</h3>
							<p className="text-xs text-gray-600 dark:text-gray-400 mt-0.5">
								Your Python program is requesting user input
							</p>
						</div>
					</div>
				</div>

				{/* Content */}
				<div className="px-5 py-4 space-y-4">
					{/* Prompt */}
					<div className="bg-gray-50 dark:bg-gray-750 rounded-lg p-3 border border-gray-200 dark:border-gray-600">
						<div className="flex items-start gap-2">
							<div className="mt-0.5">
								<svg
									className="w-4 h-4 text-python-yellow-600 dark:text-python-yellow-400"
									fill="currentColor"
									viewBox="0 0 20 20"
								>
									<path
										fillRule="evenodd"
										d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
										clipRule="evenodd"
									/>
								</svg>
							</div>
							<div className="flex-1">
								<p className="text-sm font-medium text-gray-800 dark:text-gray-200 mb-1">
									Prompt:
								</p>
								<code className="text-sm text-gray-700 dark:text-gray-300 font-mono bg-white dark:bg-gray-800 px-2 py-1 rounded border border-gray-300 dark:border-gray-600 break-all">
									{prompt}
								</code>
							</div>
						</div>
					</div>

					{/* Input Form */}
					<form onSubmit={handleSubmit} className="space-y-3">
						<div>
							<label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
								Your Response:
							</label>
							<div className="relative">
								<input
									ref={inputRef}
									type="text"
									value={inputValue}
									onChange={(e) => setInputValue(e.target.value)}
									onKeyDown={handleKeyDown}
									className="w-full px-4 py-3 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-python-blue-500 focus:border-python-blue-500 outline-none transition-all duration-200 font-mono text-sm"
									placeholder="Type your input here..."
								/>
								<div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
									<svg
										className="w-4 h-4 text-gray-400"
										fill="none"
										stroke="currentColor"
										viewBox="0 0 24 24"
									>
										<path
											strokeLinecap="round"
											strokeLinejoin="round"
											strokeWidth="2"
											d="M15 12a3 3 0 11-6 0 3 3 0 016 0z M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
										/>
									</svg>
								</div>
							</div>
						</div>
					</form>
				</div>

				{/* Footer */}
				<div className="px-5 py-4 bg-gray-50 dark:bg-gray-750 border-t border-gray-200 dark:border-gray-700 space-y-3">
					{/* Action Buttons */}
					<div className="flex gap-3 justify-end">
						<button
							onClick={onCancel}
							className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-600 hover:bg-gray-50 dark:hover:bg-gray-500 border border-gray-300 dark:border-gray-500 rounded-lg transition-all duration-200 font-medium text-sm shadow-soft hover:shadow-soft-md flex items-center gap-2"
						>
							<svg
								className="w-4 h-4"
								fill="none"
								stroke="currentColor"
								viewBox="0 0 24 24"
							>
								<path
									strokeLinecap="round"
									strokeLinejoin="round"
									strokeWidth="2"
									d="M6 18L18 6M6 6l12 12"
								/>
							</svg>
							Cancel
						</button>
						<button
							onClick={handleSubmit}
							disabled={!inputValue.trim()}
							className="px-4 py-2 bg-python-blue-600 hover:bg-python-blue-700 disabled:bg-gray-300 dark:disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded-lg transition-all duration-200 font-medium shadow-soft hover:shadow-soft-md disabled:hover:shadow-soft text-sm flex items-center gap-2"
						>
							<svg
								className="w-4 h-4"
								fill="none"
								stroke="currentColor"
								viewBox="0 0 24 24"
							>
								<path
									strokeLinecap="round"
									strokeLinejoin="round"
									strokeWidth="2"
									d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"
								/>
							</svg>
							Submit Input
						</button>
					</div>

					{/* Helpful Tips */}
					<div className="bg-python-yellow-50 dark:bg-python-yellow-900/20 border border-python-yellow-200 dark:border-python-yellow-800 rounded-lg p-3">
						<div className="flex items-start gap-2">
							<svg
								className="w-4 h-4 text-python-yellow-600 dark:text-python-yellow-400 mt-0.5 flex-shrink-0"
								fill="currentColor"
								viewBox="0 0 20 20"
							>
								<path
									fillRule="evenodd"
									d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
									clipRule="evenodd"
								/>
							</svg>
							<div className="text-xs text-python-yellow-700 dark:text-python-yellow-300">
								<p className="font-medium mb-1">Quick Tips:</p>
								<ul className="space-y-0.5 text-python-yellow-600 dark:text-python-yellow-400">
									<li>
										• Press{" "}
										<kbd className="px-1 py-0.5 bg-python-yellow-100 dark:bg-python-yellow-900/40 rounded text-xs font-mono">
											Enter ↵
										</kbd>{" "}
										to submit
									</li>
									<li>
										• Press{" "}
										<kbd className="px-1 py-0.5 bg-python-yellow-100 dark:bg-python-yellow-900/40 rounded text-xs font-mono">
											Escape
										</kbd>{" "}
										to cancel
									</li>
									<li>
										• Your input will be passed to the{" "}
										<code className="font-mono bg-python-yellow-100 dark:bg-python-yellow-900/40 px-1 rounded">
											input()
										</code>{" "}
										function
									</li>
								</ul>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
};

export default InputDialog;
